import { createApi } from "@reduxjs/toolkit/query/react";
import { EmployeeData, EmployeeFields, EmployeeFilter } from "features/employee/types/employee";
import { Roles } from "shared/types/roles";
import { Filter } from "shared/types/filters";
import { baseAppQuery } from "shared/api/baseQuery";
import { snackbarOnQueryStarted } from "middleware/snackbarOnQueryStarted";

const ENDPOINT = "/employee";
const employeesApi: any = createApi({
  reducerPath: "employeesApi",
  baseQuery: baseAppQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["employees"],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getEmployeeList: builder.query<
      EmployeeData,
      Filter & { organizationId?: string }
    >({
      query: (filter) => {
        const queryParams = new URLSearchParams();
        if (filter) {
          if (filter.pageNumber)
            queryParams.set("page", filter.pageNumber.toString());
          if (filter.pageSize)
            queryParams.set("size", filter.pageSize.toString());
          if (filter.organizationId)
            queryParams.set("organizationId", filter.organizationId);
        }
        return `${ENDPOINT}/fetch-all?${queryParams}`;
      },
      providesTags: ["employees"],
    }),
    getEmployeeListCurrent: builder.query<EmployeeData, EmployeeFilter>({
      query: (filter) => {
        const queryParams = new URLSearchParams();
        if (filter) {
          queryParams.set("page", filter.pageNumber.toString());
          queryParams.set("size", filter.pageSize.toString());
          if (filter.firstName) queryParams.set("firstName", filter.firstName);
          if (filter.lastName) queryParams.set("lastName", filter.lastName);
          if (filter.iin) queryParams.set("iin", filter.iin);
          if (filter.roleId) queryParams.set("roleId", filter.roleId);
          if (filter.status) queryParams.set("status", filter.status);
        }
        return `${ENDPOINT}/fetch-all-current?${queryParams}`;
      },
      providesTags: ["employees"],
    }),
    getEmployeeById: builder.query<EmployeeData, string>({
      query: (id) => `${ENDPOINT}/${id}`,
      providesTags: ["employees"],
    }),
    getEmployeeRoleList: builder.query<Roles, unknown>({
      query: () => `${ENDPOINT}/role/list`,
    }),
    addEmployee: builder.mutation<EmployeeData, EmployeeFields>({
      query: (data) => ({
        url: ENDPOINT,
        method: "POST",
        body: data,
      }),
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"employeeDataCreated",
        errorMessage:"employeeDataNotCreated",
      })
    }),
    lockEmployee: builder.mutation<EmployeeData, { id: string; isBlock: string }>({
      query: ({ id, isBlock }) => ({
        url: `${ENDPOINT}/lock/${id}?isBlock=${isBlock}`,
        method: "POST",
      }),
      invalidatesTags: ["employees"],
    }),
    updateEmployee: builder.mutation<EmployeeData, EmployeeFields>({
      query: (data) => ({
        url: ENDPOINT,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"employeeDataUpdated",
        errorMessage:"employeeDataNotUpdated",
      })
    }),
    deleteEmployee: builder.mutation<EmployeeData, string>({
      query: (id) => ({
        url: `${ENDPOINT}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["employees"],
    }),
    setSigner: builder.mutation<undefined, string>({
      query: (id) => ({
        url: `${ENDPOINT}/set-signer?id=${id}`,
        method: "POST",
      }),
      invalidatesTags: ["employees"],
    }),
  }),
});

export const {
  useGetEmployeeListQuery,
  useGetEmployeeListCurrentQuery,
  useGetEmployeeByIdQuery,
  useGetEmployeeRoleListQuery,
  useAddEmployeeMutation,
  useLockEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useSetSignerMutation,
} = employeesApi;
export default employeesApi;
